import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiAlertTriangle, FiUsers, FiFileText, FiClock, FiCheck, FiX } = FiIcons;

const EmployeeRelations = () => {
  const [selectedProcess, setSelectedProcess] = useState('disciplinary');

  const disciplinaryProcess = {
    title: 'Disciplinary Process',
    stages: [
      {
        stage: 'Early Intervention',
        type: 'informal',
        timeline: '0-2 weeks',
        actions: [
          'Informal discussion with employee',
          'Document concerns and discussion outcomes',
          'Set clear expectations and improvement targets',
          'Offer support and training if needed'
        ],
        compliance: [
          { type: 'acas', text: 'ACAS recommends early informal resolution' },
          { type: 'legal', text: 'Document all informal discussions for audit trail' }
        ]
      },
      {
        stage: 'Investigation',
        type: 'formal',
        timeline: '1-3 weeks',
        actions: [
          '**Suspend employee if necessary** (with pay, not disciplinary action)',
          'Appoint investigating officer (independent from line management)',
          'Gather evidence: witness statements, documents, CCTV',
          'Interview relevant parties',
          '**Prepare investigation report** with findings'
        ],
        compliance: [
          { type: 'legal', text: 'Right to be accompanied at investigatory meetings' },
          { type: 'acas', text: 'Reasonable timeframe for investigation' },
          { type: 'legal', text: 'Suspension must be on full pay unless contract specifies otherwise' }
        ]
      },
      {
        stage: 'Formal Disciplinary Hearing',
        type: 'formal',
        timeline: '2-4 weeks',
        actions: [
          '**Send disciplinary invitation letter** (minimum 48 hours notice)',
          'Include: allegations, evidence, right to be accompanied',
          'Hold hearing with independent chair',
          'Allow employee to present case and call witnesses',
          '**Adjourn to consider decision**',
          'Communicate outcome in writing within 24 hours'
        ],
        compliance: [
          { type: 'legal', text: 'Statutory right to be accompanied by trade union rep or colleague' },
          { type: 'legal', text: 'Minimum 48 hours notice required' },
          { type: 'acas', text: 'Fair hearing process with opportunity to respond' }
        ]
      },
      {
        stage: 'Disciplinary Outcomes',
        type: 'formal',
        timeline: '1-2 weeks',
        actions: [
          '**First Written Warning** (active for 6-12 months)',
          '**Final Written Warning** (active for 12-24 months)',
          '**Dismissal** (with notice or payment in lieu)',
          '**Alternative sanctions** (demotion, transfer, training)',
          'Right of appeal (5 working days to lodge)'
        ],
        compliance: [
          { type: 'legal', text: 'Progressive discipline unless gross misconduct' },
          { type: 'legal', text: 'Right of appeal must be offered' },
          { type: 'acas', text: 'Sanctions should be proportionate to misconduct' }
        ]
      },
      {
        stage: 'Appeal Process',
        type: 'formal',
        timeline: '2-3 weeks',
        actions: [
          'Employee submits appeal within 5 working days',
          'Appoint senior manager (not involved in original decision)',
          'Review original decision and new evidence',
          'Hold appeal hearing if necessary',
          '**Final decision** - uphold, reduce, or overturn'
        ],
        compliance: [
          { type: 'legal', text: 'Appeal should be heard by more senior manager' },
          { type: 'acas', text: 'Full review of original decision and process' }
        ]
      }
    ]
  };

  const grievanceProcess = {
    title: 'Grievance Process',
    stages: [
      {
        stage: 'Informal Resolution',
        type: 'informal',
        timeline: '1-2 weeks',
        actions: [
          'Employee raises concern with line manager',
          'Attempt informal resolution through discussion',
          'Document attempted resolution',
          'If unresolved, advise of formal grievance process'
        ],
        compliance: [
          { type: 'acas', text: 'Encourage informal resolution first' },
          { type: 'legal', text: 'Employee has right to raise formal grievance' }
        ]
      },
      {
        stage: 'Formal Grievance',
        type: 'formal',
        timeline: '2-4 weeks',
        actions: [
          'Employee submits written grievance',
          'Acknowledge receipt within 5 working days',
          'Appoint investigating manager (independent)',
          'Invite to grievance hearing (48 hours notice)',
          'Right to be accompanied'
        ],
        compliance: [
          { type: 'legal', text: 'Statutory right to be accompanied' },
          { type: 'legal', text: 'Must acknowledge grievance promptly' },
          { type: 'acas', text: 'Fair and thorough investigation required' }
        ]
      },
      {
        stage: 'Investigation & Hearing',
        type: 'formal',
        timeline: '3-5 weeks',
        actions: [
          'Investigate grievance thoroughly',
          'Interview relevant parties and witnesses',
          'Hold formal grievance hearing',
          'Allow employee to present case',
          'Adjourn to consider decision'
        ],
        compliance: [
          { type: 'acas', text: 'Reasonable timeframe for resolution' },
          { type: 'legal', text: 'Fair hearing with opportunity to respond' }
        ]
      },
      {
        stage: 'Outcome & Appeal',
        type: 'formal',
        timeline: '1-2 weeks',
        actions: [
          'Communicate decision in writing',
          'Explain reasons for decision',
          'Offer right of appeal',
          'Implement any agreed changes',
          'Monitor ongoing situation'
        ],
        compliance: [
          { type: 'legal', text: 'Right of appeal must be offered' },
          { type: 'acas', text: 'Follow-up to ensure resolution' }
        ]
      }
    ]
  };

  const processes = {
    disciplinary: disciplinaryProcess,
    grievance: grievanceProcess
  };

  const currentProcess = processes[selectedProcess];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Relations & Case Management</h1>
          <p className="text-gray-600">Detailed workflows for disciplinary and grievance processes</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setSelectedProcess('disciplinary')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedProcess === 'disciplinary' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Disciplinary Process
          </button>
          <button
            onClick={() => setSelectedProcess('grievance')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedProcess === 'grievance' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Grievance Process
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {currentProcess.stages.map((stage, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`px-6 py-4 ${
              stage.type === 'informal' ? 'bg-yellow-500' : 'bg-red-600'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-2">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-xl">{stage.stage}</h3>
                    <p className="text-white opacity-90 text-sm">
                      {stage.type === 'informal' ? 'Informal Stage' : 'Formal Stage'} • Timeline: {stage.timeline}
                    </p>
                  </div>
                </div>
                <SafeIcon 
                  icon={stage.type === 'informal' ? FiUsers : FiFileText} 
                  className="text-white text-2xl" 
                />
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiCheck} className="text-green-600 mr-2" />
                    Required Actions
                  </h4>
                  <ul className="space-y-2">
                    {stage.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                        <span 
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-orange-600 mr-2" />
                    Compliance Requirements
                  </h4>
                  <div className="space-y-3">
                    {stage.compliance.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-start space-x-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                          requirement.type === 'legal' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {requirement.type === 'legal' ? '⚖️ Legal' : '🟢 ACAS'}
                        </span>
                        <span className="text-gray-700 text-sm">{requirement.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-3">Key Legal Protections</h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <p><strong>Right to be Accompanied:</strong> Trade union representative or workplace colleague at formal hearings</p>
            <p><strong>Fair Process:</strong> Reasonable investigation, opportunity to respond, proportionate sanctions</p>
          </div>
          <div>
            <p><strong>Appeal Rights:</strong> Right to appeal disciplinary decisions to senior management</p>
            <p><strong>Documentation:</strong> All stages must be properly documented for potential tribunal defense</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeRelations;